import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { localTimeZone, setPreferredTimeZone } from '@/lib/timezone'
import { usePreferences } from './usePreferences'

/**
 * Applies the saved timezone to the app-wide formatters and returns the zone in effect.
 * Mounted once in the shell; its return value keys the page so times re-render when it changes.
 */
export function useTimeZonePreference(): string {
  const prefs = usePreferences()
  const queryClient = useQueryClient()
  // Set during render (not in an effect) so the very first paint of the page already uses it.
  setPreferredTimeZone(prefs.data?.timezone ?? null)
  const zone = localTimeZone()

  // Date-range results are zone-dependent (a "day" starts at a different instant), so data
  // fetched under the old zone is refetched rather than shown against the new one.
  const previous = useRef(zone)
  useEffect(() => {
    if (previous.current === zone) return
    previous.current = zone
    void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'me-preferences' })
  }, [queryClient, zone])

  return zone
}
